/**
 * Lazy-loaded "Categories" tab for Product Catalogue Review.
 *
 * Calls the lightweight classifier endpoint directly (no tag dependency),
 * so the tab is correct on the first sync without waiting for a tagger
 * run. Hands the rows to the shared DataViewTable using the same
 * categoryAlignmentConfig that powers the exception report — one column
 * / rendering source of truth.
 */
import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import { Launch as LaunchIcon } from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'

import { api } from '../../../../../services/api'
import { DataViewTable } from '../../../../../components/shared/DataViewTable'
import { categoryAlignmentConfig } from '../../../../../config/exceptions/product-setup/categoryAlignmentExceptions'

/**
 * Tool-tab variant of the category-alignment config: the exception
 * report defaults to "needs attention only" (Present rows hidden), but
 * the audit tab is a browse surface — surface every category up front
 * and let the user narrow with the same chip strip. The chips, columns
 * and renderers all stay shared with the exception config.
 */
const categoriesTabConfig = {
  ...categoryAlignmentConfig,
  summaryChips: categoryAlignmentConfig.summaryChips
    ? {
        ...categoryAlignmentConfig.summaryChips,
        defaultActiveKeys: categoryAlignmentConfig.summaryChips.categories.map(
          (c) => c.key,
        ),
      }
    : undefined,
}

interface Props {
  fp_code: string
  title?: string
}

/**
 * Row shape returned by the unified `get_master_presence_exceptions`
 * handler. Mirrors the exception-report envelope - the shared
 * `masterCompareTable` renderer reads `expected.name` / `found.name`
 * / `field_matches.name` / `issue_text` / `action_hint` / `match_link`,
 * and the appended `# Products` column reads `product_count`
 * (flattened onto the row from the classifier's pass-through metadata).
 */
interface CategoryRow {
  object_id: string
  master_value: string
  alignment_status: 'present' | 'near_match' | 'extra' | 'missing'
  expected: { name: string | null }
  found: { name: string | null }
  field_matches: { name: boolean }
  issue_text: string
  action_hint: string | null
  match_link: string | null
  matched_ls_id: string | null
  fuzzy_name_score: number | null
  product_count: number | null
}

interface CategoryResponse {
  fp_code: string
  fp_name: string
  fp_country: string
  exception_description: string | null
  rows: CategoryRow[]
  counts: {
    present: number
    near_match: number
    extra: number
    missing: number
    action_total: number
  }
}

const CategoriesTab: React.FC<Props> = ({ fp_code, title }) => {
  const [response, setResponse] = useState<CategoryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!fp_code) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get(`/inspector/inventory/tools/category-alignment/${fp_code}`)
      .then((res: any) => {
        if (cancelled) return
        setResponse(res as CategoryResponse)
      })
      .catch((err: any) => {
        if (!cancelled)
          setError(err?.message || 'Failed to load category alignment')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fp_code])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) return <Alert severity="error">{error}</Alert>

  if (!response) return null

  const rows = response.rows
  const description = response.exception_description

  const exceptionLink = `/inspect/${fp_code}/exceptions/product-setup/ex-product-ls-category-alignment`

  if (!rows.length) {
    return (
      <Alert severity="info">
        No master list for this FP's country, or no main categories
        recorded yet.
      </Alert>
    )
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        <Button
          component={RouterLink}
          to={exceptionLink}
          variant="outlined"
          size="small"
          startIcon={<LaunchIcon />}
          sx={{ flexShrink: 0, textTransform: 'none' }}
        >
          View exception report
        </Button>
      </Stack>
      <DataViewTable
        config={categoriesTabConfig}
        data={rows}
        response={response}
        variant="inspector"
        fpCode={fp_code}
        embedded
        toolbarTitle="Main category alignment"
        title={title}
      />
    </Stack>
  )
}

export default CategoriesTab
